/* eslint-disable no-plusplus */
/* eslint-disable no-use-before-define */
import axios from 'axios';
import { DirectUpload } from '@rails/activestorage';
import Turbolinks from 'turbolinks';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static values = {
    userId: String,
  };

  static targets = [
    'record',
    'pause',
    'resume',
    'cancel',
    'stop',
    'progress',
    'activeMic',
    'inactiveMic',
    'disabledMic',
    'activeMicContainer',
    'inactiveMicContainer',
    'disabledMicContainer',
    'pulser',
    'indicator',
    'uploadStatus',
    'uploadStatusCloser',
    'uploadStatusContent',
    'recordingStatus',
    'liveButton',
    'audioElement',
  ];

  connect() {
    navigator.mediaDevices.ondevicechange = (event) => {
      this.displayInfoMessage(`A change occurred without your device: ${event.name}`);
    };
  }

  uploadVoiceSignature(file, userId) {
    const input = document.getElementById('user_voice_signature');
    const url = input.dataset.directUploadUrl;
    const upload = new DirectUpload(file, url, this);

    upload.create(async (error, blob) => {
      if (error) {
        this.uploadFailure(error);
      } else {
        this.uploadSuccess();
        const hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('value', blob.signed_id);
        hiddenField.name = input.name;

        await axios
          .patch(`/users/${userId}/update_voice_signature.json`, {
            user: {
              voice_signature: blob.signed_id,
            },
          })
          .then(async (response) => {
            this.userUpdateSuccess(response);
            window.location.reload();
          })
          .catch((err) => {
            this.userUpdateFailure(err);
          });
      }
    });
  }

  uploadFailure(error) {
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('alert-danger');
    this.uploadStatusTarget.classList.remove('d-none');
    this.uploadStatusContentTarget.textContent = `Something went wrong: ${error}.`;
  }

  uploadSuccess() {
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('alert-success');
    this.uploadStatusTarget.classList.remove('d-none');
    this.uploadStatusContentTarget.textContent = 'Your voice signature was uploaded.';
  }

  userUpdateFailure(err) {
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('alert-danger');
    this.uploadStatusTarget.classList.remove('d-none');
    this.uploadStatusContentTarget.textContent = `Something went wrong when trying to save your voice signature even though the file was uploaded: ${err}.`;
  }

  userUpdateSuccess(response) {
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('alert-success');
    this.uploadStatusTarget.classList.remove('d-none');
    this.uploadStatusContentTarget.textContent = `Your voice signature was successfully saved. The page will automatically reload momentarily.`;
  }

  updateRecordingStatusIndicator() {
    const record = this.inactiveMicTarget;
    const stop = this.activeMicTarget;
    const pause = this.pauseTarget;
    const resume = this.resumeTarget;
    const cancel = this.cancelTarget;

    if (record.disabled) {
      this.liveButtonTarget.classList.remove('d-none');
      this.recordingStatusTarget.classList.remove('d-none');

      if (pause.disabled) {
        this.recordingStatusTarget.textContent = 'Paused';
        this.liveButtonTarget.classList.add('d-none');
      } else {
        this.liveButtonTarget.classList.remove('d-none');
        this.recordingStatusTarget.textContent = 'Recording';
      }
    } else if (stop.disabled && pause.disabled && resume.disabled && !cancel.disabled) {
      this.liveButtonTarget.classList.add('d-none');
      this.recordingStatusTarget.textContent = 'Stopped';
    } else if (stop.disabled && pause.disabled && resume.disabled && cancel.disabled) {
      this.liveButtonTarget.classList.add('d-none');
      this.recordingStatusTarget.textContent = 'Canceled';
    }
  }

  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener('progress', (event) => this.onProgress(event));
  }

  onProgress(event) {
    const progress = (event.loaded / event.total) * 100;
    this.progressTarget.classList.remove('d-none');
    this.indicatorTarget.style.width = `${progress}%`;
    this.indicatorTarget.textContent = `${progress}%`;
  }

  visualize() {
    // account for possible previous recorded session
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('d-none');
    this.progressTarget.classList.add('d-none');

    // setup timing visual
    this.activeMicContainerTarget.classList.remove('d-none');
    this.activeMicTarget.classList.remove('d-none');
    this.inactiveMicTarget.classList.add('d-none');
    this.inactiveMicContainerTarget.classList.add('d-none');
  }

  stopVisualizing() {
    this.activeMicContainerTarget.classList.add('d-none');
    this.activeMicTarget.classList.add('d-none');
    this.disabledMicTarget.classList.remove('d-none');
    this.disabledMicContainerTarget.classList.remove('d-none');
  }

  pauseVisualizing() {
    this.pulserTarget.classList.add('d-none');
    this.activeMicTarget.classList.remove('bi-mic-fill');
    this.activeMicTarget.classList.add('bi-mic-mute-fill');
  }

  resumeVisualizing() {
    this.pulserTarget.classList.remove('d-none');
    this.activeMicTarget.classList.remove('bi-mic-mute-fill');
    this.activeMicTarget.classList.add('bi-mic-fill');
  }

  signalFail() {
    this.pulserTarget.classList.remove('d-none');
    this.inactiveMicTarget.classList.remove('bi-mic-fill');
    this.inactiveMicTarget.classList.add('bi-headset');
    this.inactiveMicTarget.title = 'Connect a microphone or headset';
  }

  startAudioRecording() {
    const audioElement = this.audioElementTarget;
    const recorderAudioIsPlaying = !audioElement.paused;
    const record = this.inactiveMicTarget;
    const stop = this.activeMicTarget;
    const cancel = this.cancelTarget;
    const pause = this.pauseTarget;
    const resume = this.resumeTarget;

    if (recorderAudioIsPlaying) {
      audioElement.pause();
    }

    audioRecorder
      .start()
      .then(() => {
        this.timer = startTimer(0, 'timer', () => {
          alert('Done!');
        });

        this.visualize();
        record.classList.add('d-none');
        pause.classList.remove('d-none');
        stop.classList.remove('d-none');
        cancel.classList.remove('d-none');

        stop.disabled = false;
        pause.disabled = false;
        cancel.disabled = false;
        resume.disabled = true;
        record.disabled = true;

        this.updateRecordingStatusIndicator();
      })
      .catch((error) => {
        // on error
        // No Browser Support Error
        if (error.message.includes('mediaDevices API or getUserMedia method is not supported in this browser.')) {
          this.displayErrorMessage('To record audio, use browsers like Chrome and Firefox.');
        }

        switch (error.name) {
          case 'AbortError': // error from navigator.mediaDevices.getUserMedia
            this.displayErrorMessage('An AbortError has occurred.');
            break;
          case 'NotAllowedError': // error from navigator.mediaDevices.getUserMedia
            this.displayErrorMessage(
              'A NotAllowedError has occurred. You might have denied permission to access the microphone.'
            );
            break;
          case 'NotFoundError': // error from navigator.mediaDevices.getUserMedia
            this.displayErrorMessage('No recording device was found');
            break;
          case 'NotReadableError': // error from navigator.mediaDevices.getUserMedia
            this.displayErrorMessage('A NotReadableError has occurred.');
            break;
          case 'SecurityError': // error from navigator.mediaDevices.getUserMedia or from the MediaRecorder.start
            this.displayErrorMessage('A SecurityError has occurred.');
            break;
          case 'TypeError': // error from navigator.mediaDevices.getUserMedia
            this.displayErrorMessage('A TypeError has occurred.');
            break;
          case 'InvalidStateError': // error from the MediaRecorder.start
            this.displayErrorMessage('An InvalidStateError has occurred.');
            break;
          case 'UnknownError': // error from the MediaRecorder.start
            this.displayErrorMessage('An UnknownError has occurred.');
            break;
          default:
            this.displayErrorMessage(`An error occurred with the error name ${error.name}`);
        }
      });
  }

  stopAudioRecording() {
    this.timer.stop();
    const record = this.inactiveMicTarget;
    const stop = this.activeMicTarget;
    const pause = this.pauseTarget;
    const resume = this.resumeTarget;
    const cancel = this.cancelTarget;

    // record.classList.remove('d-none');
    resume.classList.add('d-none');
    pause.classList.add('d-none');
    stop.classList.add('d-none');
    cancel.classList.add('d-none');

    record.disabled = false;
    stop.disabled = true;
    pause.disabled = true;
    resume.disabled = true;

    this.updateRecordingStatusIndicator();

    audioRecorder
      .stop()
      .then((audioAsblob) => {
        this.stopVisualizing();

        const audioType = 'audio/ogg';
        const file = new File([audioAsblob], `${this.recordingNameValue}.ogg`, {
          type: audioType,
          lastModified: new Date().getTime(),
        });
        const container = new DataTransfer();
        container.items.add(file);

        this.uploadVoiceSignature(file, this.userIdValue);

        this.playAudio(audioAsblob);
      })
      .catch((error) => {
        switch (error.name) {
          case 'InvalidStateError': // error from the MediaRecorder.stop
            this.displayErrorMessage('An InvalidStateError has occurred.');
            break;
          default:
            this.displayErrorMessage(`An error occurred with the error name ${error.name}`);
        }
      });
  }

  playAudio(recorderAudioAsBlob) {
    const audioElement = this.audioElementTarget;

    const reader = new FileReader();

    reader.onload = (e) => {
      const base64URL = e.target.result;

      if (!this.audioElementSource) this.createSourceForAudioElement();

      this.audioElementSource.src = base64URL;

      const BlobType = recorderAudioAsBlob.type.includes(';')
        ? recorderAudioAsBlob.type.substr(0, recorderAudioAsBlob.type.indexOf(';'))
        : recorderAudioAsBlob.type;
      this.audioElementSource.type = BlobType;

      audioElement.load();

      audioElement.play();

      this.displayTextIndicatorOfAudioPlaying();
    };

    reader.readAsDataURL(recorderAudioAsBlob);
  }

  createSourceForAudioElement() {
    const audioElement = this.audioElementTarget;
    const sourceElement = document.createElement('source');
    audioElement.appendChild(sourceElement);

    this.audioElementSource = sourceElement;
  }

  pauseAudioRecording() {
    const record = this.inactiveMicTarget;
    const stop = this.activeMicTarget;
    const pause = this.pauseTarget;
    const resume = this.resumeTarget;

    this.timer.pause();
    this.pauseVisualizing();

    pause.classList.add('d-none');
    resume.classList.remove('d-none');
    stop.classList.remove('d-none');

    pause.disabled = true;
    stop.disabled = false;
    resume.disabled = false;
    record.disabled = true;

    this.updateRecordingStatusIndicator();

    audioRecorder.pause();
  }

  resumeAudioRecording() {
    const record = this.inactiveMicTarget;
    const stop = this.activeMicTarget;
    const pause = this.pauseTarget;
    const resume = this.resumeTarget;

    this.timer.resume();
    this.resumeVisualizing();

    resume.classList.add('d-none');
    record.classList.add('d-none');
    pause.classList.remove('d-none');
    stop.classList.remove('d-none');

    pause.disabled = false;
    resume.disabled = true;
    stop.disabled = false;
    record.disabled = true;

    this.updateRecordingStatusIndicator();

    audioRecorder.resume();
  }

  cancelAudioRecording() {
    const record = this.inactiveMicTarget;
    const stop = this.activeMicTarget;
    const pause = this.pauseTarget;
    const resume = this.resumeTarget;
    const cancel = this.cancelTarget;

    record.classList.remove('d-none');
    resume.classList.add('d-none');
    pause.classList.add('d-none');
    stop.classList.add('d-none');
    cancel.classList.add('d-none');

    record.disabled = false;
    cancel.disabled = true;
    stop.disabled = true;
    pause.disabled = true;
    resume.disabled = true;

    this.updateRecordingStatusIndicator();

    audioRecorder.cancel();
    this.stopVisualizing();
    this.timer.cancel();
    document.getElementById('refresh-page').classList.remove('d-none');
  }

  displayErrorMessage(errorMessage) {
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('alert-danger');
    this.uploadStatusTarget.classList.remove('d-none');
    this.uploadStatusContentTarget.textContent = `Error: ${errorMessage}`;
  }

  displayInfoMessage(message) {
    this.clearPreviousAlerts();
    this.uploadStatusTarget.classList.add('alert-info');
    this.uploadStatusTarget.classList.remove('d-none');
    this.uploadStatusContentTarget.textContent = `Info: ${message}`;
  }

  clearPreviousAlerts() {
    this.uploadStatusTarget.classList.remove('alert-info', 'alert-danger', 'alert-success');
  }

  displayTextIndicatorOfAudioPlaying() {
    this.displayInfoMessage('Audio playing...');
  }
}

const startTimer = (seconds, container, oncomplete) => {
  let startTime;
  let timerInterval;
  let ms = seconds * 1000;
  const display = document.getElementById(container);
  const obj = {};

  obj.resume = () => {
    startTime = new Date().getTime();
    timerInterval = setInterval(obj.step, 250);
  };

  obj.pause = () => {
    ms = obj.step();
    clearInterval(timerInterval);
  };

  obj.cancel = () => {
    ms = seconds * 1000;
    startTime = new Date().getTime();
    setTimeout(obj.step, 250);
    clearInterval(timerInterval);
  };

  obj.stop = () => {
    ms = obj.step();
    clearInterval(timerInterval);
  };

  obj.step = () => {
    const now = Math.max(0, ms + (new Date().getTime() - startTime));
    const m = Math.floor(now / 60000);
    let s = Math.floor(now / 1000) % 60;

    s = (s < 10 ? '0' : '') + s;
    display.innerHTML = `${m}:${s}`;
    if (now === 0) {
      clearInterval(timerInterval);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      obj.resume = () => {};
      if (oncomplete) oncomplete();
    }
    return now;
  };

  obj.resume();
  display.classList.remove('d-none');

  return obj;
};

// API to handle audio recording
const audioRecorder = {
  audioBlobs: [],
  mediaRecorder: null,
  streamBeingCaptured: null,
  start: () => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      return Promise.reject(new Error('mediaDevices API or getUserMedia method is not supported in this browser.'));
    }

    return navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      audioRecorder.streamBeingCaptured = stream;
      audioRecorder.mediaRecorder = new MediaRecorder(stream);
      audioRecorder.audioBlobs = [];
      audioRecorder.mediaRecorder.addEventListener('dataavailable', (event) => {
        audioRecorder.audioBlobs.push(event.data);
      });
      audioRecorder.mediaRecorder.start();
    });
  },
  stop: () =>
    new Promise((resolve) => {
      const { mimeType } = audioRecorder.mediaRecorder;
      audioRecorder.mediaRecorder.addEventListener('stop', () => {
        const audioBlob = new Blob(audioRecorder.audioBlobs, { type: mimeType });
        resolve(audioBlob);
      });
      audioRecorder.cancel();
    }),
  cancel: () => {
    audioRecorder.mediaRecorder.stop();
    audioRecorder.stopStream();
    audioRecorder.resetRecordingProperties();
  },
  pause: () => {
    audioRecorder.mediaRecorder.pause();
  },
  resume: () => {
    audioRecorder.mediaRecorder.resume();
  },
  stopStream: () => {
    audioRecorder.streamBeingCaptured.getTracks().forEach((track) => track.stop());
  },
  resetRecordingProperties: () => {
    audioRecorder.mediaRecorder = null;
    audioRecorder.streamBeingCaptured = null;
  },
};
